import React from 'react';
import { CheckCircle, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { services } from '../mock';
import * as LucideIcons from 'lucide-react';

const ServicesPage = () => {
  return (
    <div className="min-h-screen pt-20 page-transition">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-gray-50 via-white to-gray-100 relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiMwMDAwMDAiIGZpbGwtb3BhY2l0eT0iMC4wMiI+PHBhdGggZD0iTTM2IDM0djItaDJ2LTJoLTJ6bTAgNGgtMnYyaDJ2LTJ6bS00IDB2Mmg0di0yaC00em0tMiAwaC0ydjJoMnYtMnptLTItNHYyaDJ2LTJoLTJ6bTIgMHYtMmgtMnYyaDJ6bTAtNHYyaDJ2LTJoLTJ6bTItMmgydi0yaC0ydjJ6bTAgMGgydi0yaC0ydjJ6bTIgMnYtMmgydjJoLTJ6Ii8+PC9nPjwvZz48L3N2Zz4=')] opacity-40"></div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl sm:text-6xl font-serif font-bold text-gray-900 mb-6">
            Our Services
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Comprehensive painting solutions for every need. From residential interiors to large-scale commercial projects, we deliver excellence.
          </p>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-24">
            {services.map((service, index) => {
              const IconComponent = LucideIcons[service.icon];
              const isEven = index % 2 === 0;
              
              // Define object positioning for better cropping
              let objectPosition = 'object-center';
              if (service.id === 1) {
                // Interior Painting: Show chair and wall
                objectPosition = 'object-[center_70%]';
              } else if (service.id === 3) {
                // Custom Finishes: Show paint roller
                objectPosition = 'object-[50%_40%]';
              }

              return (
                <div
                  key={service.id}
                  className={`flex flex-col ${
                  isEven ? 'lg:flex-row' : 'lg:flex-row-reverse'} items-center gap-12`
                  }>

                  {/* Image Section */}
                  <div className="flex-1">
                    <div className="relative group">
                      <div className="aspect-[4/3] rounded-2xl overflow-hidden shadow-2xl">
                        <img 
                          src={service.image} 
                          alt={service.title}
                          className={`w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 ${objectPosition}`}
                        />
                      </div>
                      <div className="absolute -bottom-6 -right-6 w-32 h-32 bg-gradient-to-br from-gold-200 to-gold-300 rounded-2xl -z-10 group-hover:scale-110 transition-transform duration-300"></div>
                    </div>
                  </div>

                  {/* Content Section */}
                  <div className="flex-1">
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-gold-100 to-gold-200 mb-6">
                      {IconComponent && <IconComponent className="w-8 h-8 text-gold-700" />}
                    </div>
                    
                    <h2 className="text-4xl font-serif font-bold text-gray-900 mb-4">
                      {service.title}
                    </h2>
                    
                    <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                      {service.description}
                    </p>

                    <div className="space-y-4 mb-8">
                      {service.features.map((feature, idx) =>
                      <div key={idx} className="flex items-start space-x-3">
                          <CheckCircle className="w-6 h-6 text-gold-600 flex-shrink-0 mt-0.5" />
                          <span className="text-gray-700">{feature}</span>
                        </div>
                      )}
                    </div>

                    <Link to="/contact">
                      <Button className="bg-gradient-to-r from-gray-900 to-gray-800 hover:from-gray-800 hover:to-gray-700 text-white shadow-lg hover:shadow-xl transition-all duration-300">
                        Request Quote for {service.title}
                        <ArrowRight className="ml-2 w-5 h-5" />
                      </Button>
                    </Link>
                  </div>
                </div>);

            })}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-serif font-bold text-gray-900 mb-4">
              Why Choose European Touch
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Experience the difference of working with true professionals
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
            {
              title: 'Premium Materials',
              description: 'We use only the highest quality paints and materials from trusted brands for lasting results.'
            },
            {
              title: 'Expert Craftsmen',
              description: 'Our team consists of skilled professionals with years of experience and attention to detail.'
            },
            {
              title: 'Clean Job Sites',
              description: 'We maintain impeccable cleanliness, protecting your property and leaving no mess behind.'
            },
            {
              title: 'On-Time Completion',
              description: 'We respect your time and complete projects on schedule without compromising quality.'
            },
            {
              title: 'Free Consultations',
              description: 'Professional color consultation and project planning at no additional cost.'
            },
            {
              title: 'Warranty Protected',
              description: 'All our work is backed by comprehensive warranty for your peace of mind.'
            }].
            map((benefit, index) =>
            <Card key={index} className="border-gray-200 hover:shadow-xl transition-all duration-300 hover:border-gold-300">
                <CardContent className="p-6">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-gold-100 to-gold-200 flex items-center justify-center mb-4">
                    <CheckCircle className="w-6 h-6 text-gold-700" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">
                    {benefit.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {benefit.description}
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmZmZmYiIGZpbGwtb3BhY2l0eT0iMC4wNSI+PHBhdGggZD0iTTM2IDM0djItaDJ2LTJoLTJ6bTAgNGgtMnYyaDJ2LTJ6bS00IDB2Mmg0di0yaC00em0tMiAwaC0ydjJoMnYtMnptLTItNHYyaDJ2LTJoLTJ6bTIgMHYtMmgtMnYyaDJ6bTAtNHYyaDJ2LTJoLTJ6bTItMmgydi0yaC0ydjJ6bTAgMGgydi0yaC0ydjJ6bTIgMnYtMmgydjJoLTJ6Ii8+PC9nPjwvZz48L3N2Zz4=')] opacity-30"></div>
        
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl sm:text-5xl font-serif font-bold mb-6">
            Ready to Get Started?
          </h2>
          <p className="text-xl text-gray-300 mb-8 leading-relaxed">
            Contact us today for a free consultation and estimate
          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-gradient-to-r from-gold-500 to-gold-600 hover:from-gold-600 hover:to-gold-700 text-white px-10 py-6 text-lg shadow-2xl hover:shadow-gold-500/50 transition-all duration-300">
              Get Your Free Estimate
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>);

};

export default ServicesPage;